'use client'

/* eslint-disable @next/next/no-img-element */
import * as React from 'react'
import { useTranslations } from 'next-intl'
import type { Formats, TranslationValues } from 'next-intl'
import { tw } from 'utils/classnames'
import Image from 'next/image'
import { capitaliseFirstLetter } from 'utils/string'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { LoadWhenInViewport } from '@repo/web-ui/components/load-when-in-viewport'
import type { ProductSectionProduct } from 'cf-grpc/creativefabrica/content/v1/types_pb'

import { CheckIcon } from '@web/components/icons/check'
import { NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL } from '@web/utils/env'
import { CheckMarkClient } from './check-mark'
import {
  isCategory,
  getCategoryDetails,
  mapCategoryPageTypeToCategoryDetails,
} from '@web/components/content-api/config'
import { HoverActions } from '@web/components/hover-actions/hover-actions'

interface CardProps {
  product: {
    id: string
    authorId: string
    title: string
    slug: string
    previewPath: string
    previewWidth: number
    previewHeight: number
  }
  className?: string
  imageClass?: string
  isNotFoundPage?: boolean
}

export const CardCategory = ({ category }: { category: string }) => {
  const t = useTranslations('prod.product_description_page.discover')
  const t2 = useTranslations('prod.product_description_page.buy')

  // Fix for mismatched pngs category
  if (category === 'pngs') {
    category = 'png'
  }

  if (!isCategory(category)) {
    return null
  }

  const categoryDetails = mapCategoryPageTypeToCategoryDetails[category]
  const categoryHomepage = categoryDetails?.homepage
  const label = getCategoryDetails(
    categoryDetails?.parentId,
    t2 as <TargetKey extends string>(
      key: TargetKey,
      values?: TranslationValues | undefined,
      formats?: Partial<Formats> | undefined,
    ) => string,
  ).labelPlural

  return (
    <div className="text-12">
      {t.rich('in', {
        link: () => (
          <Link href={categoryHomepage} className="text-blue-500 hover:underline">
            {capitaliseFirstLetter(label)}
          </Link>
        ),
      })}
    </div>
  )
}

export function Card({ className, imageClass, product, isNotFoundPage }: CardProps) {
  const href = `${AppRoutes.PROD}${product.slug ? product.slug : ''}-${product.id}/`

  const imageProps = {
    alt: `${product.title || 'preview image'} thumbnail`,
    title: product.title ? product.title : '',
    src: `${NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL}/${product?.previewPath}`,
    width: product.previewWidth || 300,
    height: product.previewHeight || 300,
  }

  return (
    <div className="group relative mb-8 break-inside-avoid-column">
      <div className={tw('text-cf-dreamy-cloud-1000 flex break-inside-avoid flex-col', className)}>
        <Link href={href}>
          <Image
            {...imageProps}
            alt={imageProps.alt}
            loading="lazy"
            decoding="async"
            sizes="100vw"
            className={tw(
              'rounded-4 bg-cf-dreamy-cloud-400 mb-2 max-h-[400px] w-full scale-[0.98] transform-gpu object-cover transition-transform duration-300 ease-in-out group-hover:scale-100',
              imageClass,
            )}
            unoptimized
          />
        </Link>
        <div className="mb-1 flex items-start justify-between gap-2">
          <Link href={href} className="text-14 line-clamp-2 font-bold">
            {product.title}
          </Link>

          <LoadWhenInViewport
            fallback={
              <div className="relative z-10 flex aspect-square size-5 shrink-0 items-center rounded-full border-2 border-[#0F7B59] fill-[#0F7B59] text-[#0F7B59]">
                <CheckIcon />
              </div>
            }
          >
            <CheckMarkClient className="mt-1" isNotFoundPage={isNotFoundPage} />
          </LoadWhenInViewport>
        </div>
      </div>
      <CardCategory category={product.authorId} />

      <HoverActions id={product.id} hidePinterest />
    </div>
  )
}

export function CardList({ products }: { products: ProductSectionProduct[] | undefined }) {
  return (
    <div
      data-testid="recommendations-list"
      className="columns-1 gap-8 min-[420px]:columns-2 min-[575px]:columns-3 min-[992px]:columns-4 min-[1120px]:columns-5 min-[1280px]:columns-6 min-[1440px]:columns-7 min-[1920px]:columns-8"
    >
      {products?.map(product => (
        <Card key={product.id} product={product} />
      ))}
    </div>
  )
}
